import styled from "styled-components";
import { CardFrame, FullLine, GreySpace, WhiteSpace } from "../styles/styles";
import { CardHeadLine } from "../Molecules/Settings/CardHeadLine";
import { useTranslation } from "@coworker/locales";
import { Media } from "../types/media";
import { MediaTypes } from "../constants";
import { getMediaTypeText } from "../tools";
import { ActiveSwitcher } from "../Molecules/Settings/ActiveSwitcher";
import { Image } from "../types/images";
import { AboutSection } from "./Settings/Sections/AboutSection";
import { LocationAndSizeSection } from "./Settings/Sections/LocationAndSizeSection";
import { ImagesSection } from "./Settings/Sections/ImagesSection";
import { DeleteSection } from "./Settings/Sections/DeleteSection";
import { RadioButtonWithInput } from "../Molecules/Settings/RadioButtonWithInput";
import useFlag, { FLAGS } from "@coworker/app/src/hooks/useFlag";

const GreyBackground = styled("div")`
  background-color: var(--grey100);
  with: calc(100%);
  height: calc(100%);
`;
interface MediaSettingsFormProps {
  currentMedia: Media;
  medias: Media[];
  setCurrentMedia: (newMedia: Media) => void;
  currentCheckedMediaType: MediaTypes;
  setCurrentCheckedMediaType: (newMediaType: MediaTypes) => void;
  images: Image[];
  onImageAddStart: (fileList: FileList) => void;
  onImageAddEnd: (images: Image[]) => void;
  onImageRemove: (images: Image[]) => void;
  onImageUpdate: (image: Image) => void;
  isUpdate: boolean;
  isUploadingImages: boolean;
}

export function MediaSettingsForm({
  currentMedia,
  medias,
  setCurrentMedia,
  currentCheckedMediaType,
  setCurrentCheckedMediaType,
  images,
  onImageAddStart,
  onImageAddEnd,
  onImageRemove,
  onImageUpdate,
  isUpdate,
  isUploadingImages,
}: MediaSettingsFormProps) {
  const { t } = useTranslation();
  const enableNewMediaTypesV2 = useFlag(FLAGS.ENABLE_NEW_MEDIA_TYPES_V2);

  const radioButtonWithInput = (mediaType: MediaTypes) => (
    <>
      <RadioButtonWithInput
        media={currentMedia}
        medias={medias}
        mediaType={mediaType}
        currentCheckedMediaType={currentCheckedMediaType}
        onSave={(newMedia) => setCurrentMedia(newMedia)}
        onChange={() => {
          setCurrentMedia({ ...currentMedia, mediaType: mediaType });
          setCurrentCheckedMediaType(mediaType);
        }}
      />
    </>
  );

  return (
    <GreyBackground>
      <CardFrame>
        <GreySpace />
        <CardHeadLine
          character={"1"}
          headline={t("selectMediaTypeString")}
          subHeadline={t("mandatoryString")}
        />
        <WhiteSpace height="10" />
        {currentMedia.mediaType !== MediaTypes.ROOM ? (
          <>
            {!(isUpdate && currentMedia.mediaType === MediaTypes.HOME) ? (
              <>
                {radioButtonWithInput(MediaTypes.ROOM_SETTINGS)}
                {radioButtonWithInput(MediaTypes.VIGNETTE)}
                {radioButtonWithInput(MediaTypes.COMPACT)}
                {radioButtonWithInput(MediaTypes.COORDINATION_MEDIA)}
                {!isUpdate && radioButtonWithInput(MediaTypes.HOME)}
                {radioButtonWithInput(MediaTypes.VITALITY)}
                {radioButtonWithInput(MediaTypes.CROSS_OVER)}
                {radioButtonWithInput(MediaTypes.SHOW_CASE)}
                {enableNewMediaTypesV2 &&
                  radioButtonWithInput(MediaTypes.PERIMETER_BREAK)}
                {enableNewMediaTypesV2 &&
                  radioButtonWithInput(MediaTypes.STATEMENT_MEDIA)}
                {enableNewMediaTypesV2 &&
                  radioButtonWithInput(MediaTypes.ALL_PARTS_STUDIO)}
              </>
            ) : (
              <>{radioButtonWithInput(MediaTypes.HOME)}</>
            )}
          </>
        ) : (
          <>{radioButtonWithInput(MediaTypes.ROOM)}</>
        )}
      </CardFrame>
      <GreySpace />
      <CardFrame>
        <CardHeadLine
          character={"#"}
          headline={t(`statusOfString`, {
            name: getMediaTypeText(currentMedia.mediaType as MediaTypes),
          })}
        />
        <FullLine />
        <ActiveSwitcher
          media={currentMedia}
          onSave={(newMedia) => setCurrentMedia(newMedia)}
        />
        <WhiteSpace height={"12"} />
      </CardFrame>
      <GreySpace />
      <AboutSection
        media={currentMedia}
        onSave={(newMedia) => setCurrentMedia(newMedia)}
      />
      <GreySpace />
      <LocationAndSizeSection
        media={currentMedia}
        onSave={(newMedia) => setCurrentMedia(newMedia)}
      />
      <GreySpace />
      <ImagesSection
        isUploadingImages={isUploadingImages}
        media={currentMedia}
        images={images}
        onImageAddStart={onImageAddStart}
        onImageAddEnd={onImageAddEnd}
        onImageRemove={onImageRemove}
        onImageUpdate={onImageUpdate}
      />
      <GreySpace />
      {isUpdate && <DeleteSection currentMedia={currentMedia} />}
      <GreySpace />
    </GreyBackground>
  );
}
